import React, { useState, useRef } from 'react';
import { 
    Dialog, DialogTitle, DialogContent, DialogActions, 
    Button, Box, Typography, Divider, IconButton, 
    Table, TableBody, TableCell, TableContainer, TableHead, TableRow,
    Paper, CircularProgress, Alert, Chip
} from '@mui/material';
import { RiFileExcelLine, RiCloseLine, RiUpload2Line, RiCheckLine, RiErrorWarningLine } from "react-icons/ri";
import * as XLSX from 'xlsx';
import axios from 'axios';
import { toast } from 'react-toastify';
import { API_BASE_URL } from '../../../App';

const ImportModal = ({ open, onClose, onRefresh }) => {
    const [fileData, setFileData] = useState([]);
    const [loading, setLoading] = useState(false);
    const [importing, setImporting] = useState(false);
    const [error, setError] = useState(null);
    const fileInputRef = useRef(null);

    const handleFileChange = (e) => {
        const file = e.target.files[0];
        if (!file) return;

        setLoading(true);
        setError(null);

        const reader = new FileReader();
        reader.onload = (evt) => {
            try {
                const bstr = evt.target.result;
                const wb = XLSX.read(bstr, { type: 'binary' });
                const wsname = wb.SheetNames[0];
                const ws = wb.Sheets[wsname];
                const data = XLSX.utils.sheet_to_json(ws);
                
                // Transform data into our expected structure
                const normalizedData = data.map(row => ({
                    type: row['Typ']?.toString().toUpperCase() || 'H',
                    lastName: row['Nachname'] || '',
                    firstName: row['Vorname'] || '',
                    email: row['E-Mail'] || '',
                    role: parseInt(row['Rolle']) || 0,
                    bookingLimit: parseInt(row['Limit']) || 2,
                    headEmail: row['Haupt_Email'] || '',
                    status: 'valid' // Initial status
                }));

                // Simple validation
                normalizedData.forEach(row => {
                    if (!row.firstName || !row.lastName) row.status = 'error';
                    if (row.type === 'H' && !row.email) row.status = 'error';
                    if (row.type === 'F' && !row.headEmail) row.status = 'error';
                });

                setFileData(normalizedData);
            } catch (err) {
                console.error("File parsing error:", err);
                setError("Die Datei konnte nicht gelesen werden. Bitte prüfe das Format.");
            } finally {
                setLoading(false);
            }
        };
        reader.readAsBinaryString(file);
    };

    const handleImport = async () => {
        setImporting(true);
        try {
            const response = await axios.post(`${API_BASE_URL}/api/players/bulk-import`, {
                members: fileData
            }, { withCredentials: true });
            
            const { results } = response.data;
            toast.success(`${results.success} Mitglieder erfolgreich importiert.`);
            if (results.failed > 0) {
                toast.warning(`${results.failed} Einträge fehlgeschlagen. Siehe Log.`);
            }
            
            onRefresh();
            onClose();
            setFileData([]);
        } catch (err) {
            console.error("Import failed:", err);
            toast.error(err.response?.data?.message || "Fehler beim Importieren der Mitglieder.");
        } finally {
            setImporting(false);
        }
    };

    const reset = () => {
        setFileData([]);
        setError(null);
        if (fileInputRef.current) fileInputRef.current.value = '';
    };

    return (
        <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
            <DialogTitle sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <RiUpload2Line size={24} color="#d32f2f" />
                    <Typography variant="h6">Mitglieder Massen-Import</Typography>
                </Box>
                <IconButton onClick={onClose} size="small">
                    <RiCloseLine />
                </IconButton>
            </DialogTitle>
            <Divider />
            <DialogContent sx={{ py: 3 }}>
                {fileData.length === 0 ? (
                    <Box 
                        sx={{ 
                            border: '2px dashed #ccc', 
                            borderRadius: 2, 
                            p: 5, 
                            textAlign: 'center',
                            cursor: 'pointer',
                            '&:hover': { bgcolor: '#f8f9fa', borderColor: 'primary.main' }
                        }}
                        onClick={() => fileInputRef.current?.click()}
                    >
                        <RiFileExcelLine size={48} color="#ccc" />
                        <Typography sx={{ mt: 2 }} color="text.secondary">
                            Klicke hier oder ziehe eine Excel/CSV Datei hinein
                        </Typography>
                        <Typography variant="caption" color="text.secondary" display="block">
                            Spalten: Typ (H/F), Nachname, Vorname, E-Mail, Rolle, Limit, Haupt_Email
                        </Typography>
                        <input 
                            type="file" 
                            hidden 
                            ref={fileInputRef} 
                            accept=".xlsx, .xls, .csv" 
                            onChange={handleFileChange} 
                        />
                    </Box>
                ) : (
                    <Box>
                        <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 2 }}>
                            <Typography variant="subtitle2" sx={{ fontWeight: 'bold' }}>
                                Vorschau: {fileData.length} Einträge gefunden
                            </Typography>
                            <Button size="small" onClick={reset} color="error">Datei entfernen</Button>
                        </Box>
                        
                        <TableContainer component={Paper} variant="outlined" sx={{ maxHeight: 400 }}>
                            <Table stickyHeader size="small">
                                <TableHead>
                                    <TableRow>
                                        <TableCell>Typ</TableCell>
                                        <TableCell>Name</TableCell>
                                        <TableCell>Email / Haupt-Email</TableCell>
                                        <TableCell align="center">Limit</TableCell>
                                        <TableCell align="center">Status</TableCell>
                                    </TableRow>
                                </TableHead>
                                <TableBody>
                                    {fileData.map((row, index) => (
                                        <TableRow key={index}>
                                            <TableCell>
                                                <Chip 
                                                    label={row.type === 'H' ? 'Haupt' : 'Familie'} 
                                                    size="small" 
                                                    color={row.type === 'H' ? 'primary' : 'default'}
                                                    variant="outlined"
                                                />
                                            </TableCell>
                                            <TableCell>{row.lastName} {row.firstName}</TableCell>
                                            <TableCell>
                                                <Typography variant="body2" color="text.primary">{row.email}</Typography>
                                                {row.headEmail && <Typography variant="caption" color="text.secondary">zu: {row.headEmail}</Typography>}
                                            </TableCell>
                                            <TableCell align="center">{row.bookingLimit}</TableCell>
                                            <TableCell align="center">
                                                {row.status === 'valid' ? (
                                                    <RiCheckLine color="green" />
                                                ) : (
                                                    <RiErrorWarningLine color="red" title="Unvollständige Daten" />
                                                )}
                                            </TableCell>
                                        </TableRow>
                                    ))}
                                </TableBody>
                            </Table>
                        </TableContainer>
                    </Box>
                )}

                {error && <Alert severity="error" sx={{ mt: 2 }}>{error}</Alert>}
                {loading && <Box sx={{ display: 'flex', justifyContent: 'center', mt: 2 }}><CircularProgress /></Box>}
            </DialogContent>
            <Divider />
            <DialogActions sx={{ p: 2 }}>
                <Button onClick={onClose} color="inherit">Abbrechen</Button>
                <Button 
                    variant="contained" 
                    color="primary" 
                    disabled={fileData.length === 0 || importing || fileData.some(r => r.status === 'error')}
                    onClick={handleImport}
                    startIcon={importing ? <CircularProgress size={20} color="inherit" /> : <RiUpload2Line />}
                >
                    {importing ? 'Importiere...' : 'Import starten'}
                </Button>
            </DialogActions>
        </Dialog>
    );
};

export default ImportModal;
